import { Col, Row, Form, Button, Spinner, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { ClaimFileDto, ClaimFormDto, ClaimFollowUpCommentDto } from "../../types";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { editClaimFollowUpComment, getComementById, saveClaimFollowUpComment } from "../../api/ClaimFollowUpCommentAPI";
import { useClaimFollowUpTypesSelect } from "../../../admin/Dictionaries/hooks/useClaimFollowUpTypesSelect";
import ClaimAttachDocumentsModal from "../modals/ClaimAttachDocumentsModal";
import SelectedFilterChip from "../../../../components/Filters/components/SelectedFilterChip";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";

interface ClaimFollowUpCommentFormProps {
    claim: ClaimFormDto;
    onCancel: () => void;
    commentId?: number | null;
    professionalId?: number;
    professionalName?: string;
    setReplyEmail?: React.Dispatch<React.SetStateAction<boolean>>;
    onSaved?: () => void;
}

export default function ClaimFollowUpCommentForm({ claim, onCancel, onSaved, commentId }: ClaimFollowUpCommentFormProps) {
    const { t } = useTranslation(["claim", "common"]);

    const { data: claimFollowUpTypes } = useClaimFollowUpTypesSelect({});
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<ClaimFollowUpCommentDto>({
        comment: "",
        claimId: claim.id
    });
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [showAttachDocumentsModal, setShowAttachDocumentsModal] = useState(false);


    useEffect(() => {
        if (commentId) {
            getComementById(commentId)
                .then(data => setFormData(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [commentId]);

    const possibleClaimFiles = commentId
        ? claim.files.filter(
            file => !formData.claimFiles?.some(
                commentFile => commentFile.id === file.id
            )
        )
        : claim.files;

    const normalizeFiles = (files?: File | File[]) => {
        if (!files) return [];
        return Array.isArray(files) ? files : [files];
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setFormData((prev) => {
            const existing = normalizeFiles(prev.files);
            return {
                ...prev,
                files: [...existing, ...files],
            };
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };


    const handleRemoveFile = (indexToRemove: number) => {
        const nextFiles = (formData.files || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            files: nextFiles.length > 0 ? nextFiles : undefined,
        }));
    };

    const handleRemoveClaimFile = (indexToRemove: number) => {
        const nextFiles = (formData.claimFiles || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            claimFiles: nextFiles.length > 0 ? nextFiles : undefined,
        }));
    };

    const handleSaveForm = async () => {
        try {
            const newErrors: { comment?: string } = {};

            if (!formData.comment.trim()) {
                newErrors.comment = t("claim:followUpComment.addCommentFieldRequired");
            }
            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }
            setLoading(true);

            if (commentId) {
                await editClaimFollowUpComment(commentId.toString(), formData);
            } else {
                await saveClaimFollowUpComment(claim.id, formData);
            }

            setLoading(false);

            if (onSaved) {
                onSaved();
            } else {
                showAlert({
                    message: t("claim:followUpComment.addCommentSavedSuccesfully"),
                    type: "success",
                    duration: 5000,
                });
            }
        } catch {
            setLoading(false);
            showAlert({
                message: t("common:errors.networkError"),
                type: "danger",
                duration: 3000,
            });
        }
    };

    const addAttachedDocuments = (data: {
        selectedClaimFiles?: ClaimFileDto[];
        addedFiles?: File[];
    }) => {
        setFormData((prev) => {
            return {
                ...prev,
                files: [
                    ...(prev.files || []),
                    ...(data.addedFiles || [])
                ],
                claimFiles: [
                    ...(prev.claimFiles || []),
                    ...(data.selectedClaimFiles || [])
                ]
            };
        });
    };

    return (
        <div className="active-section">
            <Row className="w-100">
                <ClaimAttachDocumentsModal
                    claimId={claim.id}
                    show={showAttachDocumentsModal}
                    possibleClaimFiles={possibleClaimFiles}
                    onClose={() => setShowAttachDocumentsModal(false)}
                    onSubmit={addAttachedDocuments}
                />
                 
                <Row>
                    {loading && (
                        <div className="overlay-spinner">
                            <Spinner animation="border" variant="light" />
                        </div>
                    )}
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Row>

                <Row>
                    <Col xs={6} md={6}>
                        <Form.Label>{t("claim:followUpComment.titleDemandTypeSelect")}</Form.Label>
                        <Form.Select
                            className="form-control"
                            value={formData.typeId ?? ""}
                            onChange={(e) =>
                                setFormData((prev) => {
                                    return {
                                        ...prev,
                                        typeId: Number(e.target.value)
                                    };
                                })
                            }
                        >
                            <option value=""></option>
                            {claimFollowUpTypes?.map((claimFollowUpType) => (
                                <option key={claimFollowUpType.id} value={claimFollowUpType.id}>
                                    {claimFollowUpType.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>

                <Row>
                    <Form.Group>
                        <Form.Label>{t("claim:detail.emailMessage")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={8}
                            value={formData.comment}
                            isInvalid={!!errors.comment}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    comment: e.target.value,
                                })
                            }
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.comment}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Col xs={12} md={12}>
                        <Form.Control
                            className="d-none"
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple
                        />
                        <Row className="mt-3">
                            <Col md={10}>
                                {((formData.files || []).length > 0 || (formData.claimFiles || []).length > 0) &&
                                    <Stack direction="horizontal" gap={2} className="d-flex align-items-center gap-2 flex-wrap">
                                        <span><strong>{t("claim:followUpComment.fileListAttachment")}</strong></span>
                                        {(formData.files || []).map((file, index) => {
                                            return (
                                                <SelectedFilterChip
                                                    key={`${file.name}-${index}`}
                                                    label=""
                                                    value={file.name}
                                                    onRemove={() =>
                                                        handleRemoveFile(index)
                                                    }
                                                />
                                            );
                                        })}
                                        {(formData.claimFiles || []).map((file, index) => {
                                            return (
                                                <SelectedFilterChip
                                                    key={`${file.fileName}-${index}`}
                                                    label=""
                                                    value={file.fileName}
                                                    onRemove={() =>
                                                        handleRemoveClaimFile(index)
                                                    }
                                                />
                                            );
                                        })}
                                    </Stack>
                                }
                            </Col>
                            <Col md={2} className="d-flex justify-content-end align-items-start">
                                <a href="#" className="ms-auto" onClick={() => setShowAttachDocumentsModal(true)}>
                                    <p className="text-secondary mb-0">
                                        {t("claim:followUpReply.buttonAttachment")}
                                    </p>
                                </a>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col className="text-end">
                        <Button variant="secondary" onClick={onCancel}>
                            {t("common:buttons.buttonCancel")}
                        </Button>
                        <Button variant="primary" className="ms-2" onClick={() => handleSaveForm()}>
                            {t("common:buttons.buttonSave")}
                        </Button>
                    </Col>
                </Row>
            </Row>
        </div>
    );
}

